import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Mail, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Eyebrow } from "@/components/PageUi";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Phase = "loading" | "choose-window" | "starting-backfill" | "success" | "error";

const WINDOW_OPTIONS: { value: string; days: number; label: string }[] = [
  { value: "1", days: 1, label: "Last 24 hours" },
  { value: "7", days: 7, label: "Last 7 days" },
  { value: "30", days: 30, label: "Last 30 days" },
  { value: "90", days: 90, label: "Last 90 days" },
];

const DEFAULT_WINDOW = "7";

/** Map a `reason` code from Dolfin's `?gmailStatus=error&reason=…` 302 to a user-facing message. */
function messageForReason(reason: string | null): string {
  switch (reason) {
    case "access_denied":
      return "You declined the permission. To use Gmail import, click Connect Gmail again and approve the read-only access prompt.";
    case "exchange_failed":
      return "We couldn't complete the connection with Google. Please start the flow again from settings.";
    case "missing_code":
      return "The connect flow didn't return an authorization code. Please try connecting again.";
    default:
      return reason
        ? `Google returned an error during the connect flow: ${reason}. Please try again.`
        : "We couldn't complete the Gmail connection. Please try connecting again.";
  }
}

export interface GmailOAuthCallbackPageProps {
  /** Carries Dolfin's result (gmailStatus=…), never a raw OAuth code/state. Defaults to `window.location.search`. */
  search?: string;
  /** Called with the new connection id once the backfill has started, after a short success delay. */
  onConnected: (connectionId: string) => void;
  /** Called from the error state's "Back to settings" button. Omit to hide it. */
  onBackToSettings?: () => void;
  /** Delay before `onConnected` fires after success, in ms. Defaults to 1200. */
  redirectDelayMs?: number;
}

/** Dolfin has already finalised the connection and 302'd here; this page picks a backfill window and starts it. */
export function GmailOAuthCallbackPage({
  search,
  onConnected,
  onBackToSettings,
  redirectDelayMs = 1200,
}: GmailOAuthCallbackPageProps) {
  const api = useDolfinClient();
  const params = useMemo(
    () => new URLSearchParams(search ?? window.location.search),
    [search],
  );
  const [phase, setPhase] = useState<Phase>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState<string>(DEFAULT_WINDOW);
  const ran = useRef(false);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const gmailStatus = params.get("gmailStatus");
    const id = params.get("connectionId");
    const reason = params.get("reason");

    if (gmailStatus === "connected" && id) {
      setConnectionId(id);
      setPhase("choose-window");
      return;
    }

    if (gmailStatus === "error") {
      setErrorMessage(messageForReason(reason));
      setPhase("error");
      return;
    }

    // No recognised result — e.g. the page was opened directly.
    setErrorMessage(
      "The connect link is missing required parameters. Please start the flow again from settings.",
    );
    setPhase("error");
  }, [params]);

  async function startBackfill() {
    if (!connectionId) return;
    setPhase("starting-backfill");

    const days = Number(windowDays);
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    // Connection is live regardless of backfill outcome; surface the failure so the
    // user knows to retry from the candidates page.
    const { error: backfillError } = await api.POST("/v1/gmail-connections/{id}/backfill", {
      params: { path: { id: connectionId } },
      body: { since, until: null },
    });

    if (backfillError) {
      toast.warning(
        apiErrorMessage(
          backfillError,
          "Mailbox connected, but the backfill didn't start. Use \"Sync now\" from the review queue.",
        ),
      );
    }

    setPhase("success");
    if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current);
    redirectTimeoutRef.current = setTimeout(() => {
      redirectTimeoutRef.current = null;
      onConnected(connectionId);
    }, redirectDelayMs);
  }

  return (
    <div className="flex h-full items-center justify-center px-6 py-10">
      {phase === "error" ? (
        <Panel
          tone="warn"
          icon={AlertTriangle}
          title="Couldn't connect Gmail"
          body={errorMessage}
          footer={
            onBackToSettings ? (
              <Button variant="outline" onClick={onBackToSettings}>
                Back to settings
              </Button>
            ) : null
          }
        />
      ) : phase === "choose-window" ? (
        <Panel
          tone="ok"
          icon={CheckCircle2}
          title="Gmail connected"
          body="How far back should Dolfin look for bill attachments? You can import more history later."
          footer={
            <div className="flex w-full flex-col gap-3 @sm:flex-row @sm:items-center">
              <Select value={windowDays} onValueChange={setWindowDays}>
                <SelectTrigger id="backfill-window" aria-label="How far back to import" className="@sm:flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOW_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={startBackfill}>Start import</Button>
            </div>
          }
        />
      ) : phase === "success" ? (
        <Panel
          tone="ok"
          icon={CheckCircle2}
          title="Import started"
          body="Scanning your inbox now. Taking you to the review queue."
          footer={
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" /> Redirecting...
            </span>
          }
        />
      ) : (
        <Panel
          icon={Mail}
          title="Finishing connection"
          body={phase === "loading" ? "Confirming your Gmail connection." : "Starting the import."}
          footer={
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" /> This only takes a moment.
            </span>
          }
        />
      )}
    </div>
  );
}

function Panel({
  icon: Icon,
  title,
  body,
  footer,
  tone,
}: {
  icon: LucideIcon;
  title: string;
  body: ReactNode;
  footer?: ReactNode;
  tone?: "ok" | "warn";
}) {
  return (
    <section className="@container w-full max-w-md rounded-xl border bg-card/60 p-6 backdrop-blur-sm">
      <span
        className={cn(
          "grid size-10 place-items-center rounded-xl border",
          tone === "ok"
            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600"
            : tone === "warn"
              ? "border-amber-500/30 bg-amber-500/10 text-amber-600"
              : "bg-muted/50 text-muted-foreground",
        )}
      >
        <Icon className="size-5" />
      </span>
      <Eyebrow className="mt-4">Gmail</Eyebrow>
      <h1 className="mt-1 text-xl font-bold tracking-tight">{title}</h1>
      <p className="mt-1 text-sm text-pretty text-muted-foreground">{body}</p>
      {footer ? <div className="mt-5 flex justify-end">{footer}</div> : null}
    </section>
  );
}
